import { Component, Inject, OnDestroy, InjectionToken } from "@angular/core";
import { CommonModule } from "@angular/common";
import { BottomSheetComponent } from "../bottom-sheet/bottom-sheet.component";
import { SupportCardComponent } from "./components/support-card/support-card.component";
import { LogViewerModalComponent } from "./components/log-viewer-modal/log-viewer-modal.component";
import { environment } from "../../../core/config/environment";
import { DiagnosticService } from "../../../shared/services/diagnostic.service";
import { LoggerService } from "../../../shared/services/logger.service";
import { UiToastService } from "../toast/ui-toast.service";
import { NetworkStatusService } from "../../../shared/services/network-status.service";
import { Subscription } from "rxjs";
import SyncManager from "../../../core/sync/sync-manager";
import SyncStatusService from "../../../core/sync/sync-status-service";
import { ProgressBar } from "primeng/progressbar";
import { ConfirmDialog } from "primeng/confirmdialog";
import { ConfirmationService } from "primeng/api";
import { AuthService } from "../../../core/authentication/auth.service";
import { hash } from "../../../shared/utils/hashing-helper";
import { formatSyncStepLabel } from "../../../shared/utils/formatting-helper";
import { MessageModule } from "primeng/message";
import { DatabaseService } from "../../../core/database/database.service";
import { getEnvironmentName } from "../../utils/environment-helper";


export const WINDOW = new InjectionToken<Window>("Window", {
  factory: () => window
});

@Component({
  selector: "app-support-footer",
  standalone: true,
  imports: [CommonModule, BottomSheetComponent, ConfirmDialog, MessageModule, SupportCardComponent, LogViewerModalComponent, ProgressBar],
  templateUrl: "./support-footer.component.html",
  styleUrls: ["./support-footer.component.scss"],
  providers: [ConfirmationService]
})
export class SupportFooterComponent implements OnDestroy {
  isSupportSheetOpen: boolean = false;
  isCollectingDiagnostics: boolean = false;
  isResettingConnection: boolean = false;
  isClearingDeviceData: boolean = false;
  isForceSyncing: boolean = false;
  forceSyncSuccess: boolean = false;
  isOnline: boolean = true;
  browserSupportsServiceWorkers = false;
  userId: string = "unknown";
  uploadPercentage = 0;
  forceSyncProgress = 0;
  recentSyncSteps: string[] = [];
  isLogViewerOpen: boolean = false;

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private forceSyncSuccessTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly forceSyncSuccessDisplayMs: number = 3000;
  private readonly longPressDelay: number = 600;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private readonly movementThreshold: number = 10;
  private isLongPressActive: boolean = false;
  private networkStatusSubscription?: Subscription;
  private uploadProgressSubscription?: Subscription;
  private syncProgressSubscription?: Subscription;
  private forceSyncCompletedSubscription?: Subscription;
  private forceSyncFailedSubscription?: Subscription;
  protected readonly isNonProductionEnvironment = getEnvironmentName() !== "production";

  constructor(
    @Inject(WINDOW) private window: Window, // 2. Inject the window
    private readonly diagnosticService: DiagnosticService,
    private readonly logger: LoggerService,
    private readonly toast: UiToastService,
    private readonly networkService: NetworkStatusService,
    private readonly syncManager: SyncManager,
    private readonly syncStatusService: SyncStatusService,
    private readonly confirmationService: ConfirmationService,
    private readonly authService: AuthService,
    private readonly databaseService: DatabaseService
  ) {
    this.networkStatusSubscription = this.networkService.online$.subscribe(val => {
      this.isOnline = val;
    });
    this.uploadProgressSubscription = this.diagnosticService.onUploadProgress.subscribe(percent => {
      this.uploadPercentage = percent;
    });
    this.syncProgressSubscription = this.syncStatusService.syncProgressChanged.subscribe(progress => {
      this.forceSyncProgress = progress;
      this.recentSyncSteps = this.syncStatusService.getRecentCompletedSteps(5).map(formatSyncStepLabel);
    });
    this.browserSupportsServiceWorkers = this.syncManager.doesSupportServiceWorker;

    // manuallyTriggerSync's promise can resolve before the sync it actually queues/retries
    // finishes (e.g. when it aborts and re-triggers an in-progress sync), so completion/failure
    // is tracked via these emitters instead - guarded by isForceSyncing so background/other syncs
    // don't affect this button's state.
    this.forceSyncCompletedSubscription = this.syncManager.onSyncCompleted.subscribe(() => {
      if (this.isForceSyncing) {
        this.isForceSyncing = false;
        this.showForceSyncSuccess();
      }
    });
    this.forceSyncFailedSubscription = this.syncManager.onSyncFailed.subscribe(() => {
      if (this.isForceSyncing) {
        this.isForceSyncing = false;
        this.toast.error(
          "Failed to force sync",
          "Please try again or contact support if the problem persists."
        );
      }
    });

    const account = this.authService.currentAccount;
    if (account && account.localAccountId) {
      hash(account.localAccountId.toLowerCase()).then(hashedId => {
        this.userId = hashedId.toLowerCase();
      });
    }
  }

  get appVersion(): string {
    return environment.version;
  }

  // recentSyncSteps is newest-first; reverse for display so the newest step appears at the
  // bottom like a scrolling log, while opacity still fades out based on actual recency.
  get displayedSyncSteps(): { label: string; opacity: number }[] {
    return this.recentSyncSteps
      .map((label, i) => ({ label, opacity: 1 - (i * 0.18) }))
      .reverse();
  }

  ngOnDestroy(): void {
    this.cancelLongPress();
    this.clearForceSyncSuccessTimer();
    this.networkStatusSubscription?.unsubscribe();
    this.uploadProgressSubscription?.unsubscribe();
    this.syncProgressSubscription?.unsubscribe();
    this.forceSyncCompletedSubscription?.unsubscribe();
    this.forceSyncFailedSubscription?.unsubscribe();
  }

  // Long-press handlers for footer
  onFooterTouchStart(event: TouchEvent): void {
    event.preventDefault();

    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.isLongPressActive = false;
    this.startLongPress();
  }

  onFooterTouchEnd(event: TouchEvent): void {
    // If long-press was triggered, prevent any click events
    if (this.isLongPressActive) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.cancelLongPress();
  }

  onFooterTouchCancel(_event: TouchEvent): void {
    this.cancelLongPress();
  }

  onFooterTouchMove(event: TouchEvent): void {
    if (!this.longPressTimer) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      this.cancelLongPress();
      return;
    }

    const deltaX = Math.abs(touch.clientX - this.touchStartX);
    const deltaY = Math.abs(touch.clientY - this.touchStartY);

    // Cancel if user moved too much (prevents accidental triggers during scroll)
    if (deltaX > this.movementThreshold || deltaY > this.movementThreshold) {
      this.cancelLongPress();
    }
  }

  onFooterMouseDown(event: MouseEvent): void {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();

    this.touchStartX = event.clientX;
    this.touchStartY = event.clientY;
    this.isLongPressActive = false;
    this.startLongPress();
  }

  onFooterMouseUp(event: MouseEvent): void {
    // If long-press was triggered, prevent any click events
    if (this.isLongPressActive) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.cancelLongPress();
  }

  onFooterMouseLeave(_event: MouseEvent): void {
    this.cancelLongPress();
  }

  onFooterMouseMove(event: MouseEvent): void {
    if (!this.longPressTimer || event.buttons !== 1) {
      return;
    }

    const deltaX = Math.abs(event.clientX - this.touchStartX);
    const deltaY = Math.abs(event.clientY - this.touchStartY);

    if (deltaX > this.movementThreshold || deltaY > this.movementThreshold) {
      this.cancelLongPress();
    }
  }

  /**
   * Prevents click events after a long-press has been triggered
   * This ensures the long-press action doesn't also trigger a click
   */
  onFooterClick(event: MouseEvent | TouchEvent): void {
    if (this.isLongPressActive) {
      event.preventDefault();
      event.stopPropagation();
      // Reset the flag after a short delay to allow normal clicks after
      setTimeout(() => {
        this.isLongPressActive = false;
      }, 100);
    }
  }

  private startLongPress(): void {
    this.cancelLongPress();

    this.longPressTimer = setTimeout(() => {
      this.isLongPressActive = true;
      this.openSupportSheet();
      this.longPressTimer = null;
    }, this.longPressDelay);
  }

  private cancelLongPress(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    this.isLongPressActive = false;
  }

  private openSupportSheet(): void {
    this.isSupportSheetOpen = true;
  }

  onSupportSheetClose(isOpen: boolean): void {
    this.isSupportSheetOpen = isOpen;
  }

  async onSendDiagnosticsClick(): Promise<void> {
    if (this.isCollectingDiagnostics || this.isResettingConnection || !this.isOnline) {
      return;
    }
    this.isCollectingDiagnostics = true;
    this.uploadPercentage = 0;
    this.logger.info("Starting diagnostic data collection and upload...");

    try {
      await this.diagnosticService.uploadDiagnosticBundle();
      this.logger.info("Diagnostic bundle uploaded successfully");
      this.toast.success("Diagnostic data successfully sent to Support.");
    } catch (error) {
      this.logger.error("Failed to upload diagnostic bundle:", error);
      this.toast.error(
        "Failed to send diagnostic data",
        "Please try again or contact support if the problem persists."
      );
    } finally {
      this.isCollectingDiagnostics = false;
    }
  }

  async onForceSyncClick(): Promise<void> {
    if (this.isForceSyncing || !this.isOnline) {
      return;
    }
    this.isForceSyncing = true;
    this.forceSyncProgress = 0;
    this.recentSyncSteps = [];
    this.clearForceSyncSuccessTimer();
    this.forceSyncSuccess = false;
    this.logger.info("[SupportFooter] Starting manual force sync...");

    await this.syncManager.manuallyTriggerSync(true);

    // onSyncCompleted/onSyncFailed (see constructor) normally reset isForceSyncing by now, but
    // SyncManager doesn't emit either when there was simply nothing to sync - fall back to a
    // quiet success instead of leaving the button stuck mid-sync.
    if (this.isForceSyncing) {
      this.isForceSyncing = false;
      this.showForceSyncSuccess();
    }
  }

  private showForceSyncSuccess(): void {
    this.forceSyncSuccess = true;
    this.clearForceSyncSuccessTimer();
    this.forceSyncSuccessTimer = setTimeout(() => {
      this.forceSyncSuccess = false;
      this.forceSyncSuccessTimer = null;
    }, this.forceSyncSuccessDisplayMs);
  }

  private clearForceSyncSuccessTimer(): void {
    if (this.forceSyncSuccessTimer) {
      clearTimeout(this.forceSyncSuccessTimer);
      this.forceSyncSuccessTimer = null;
    }
  }

  onViewLogsClick(): void {
    this.isLogViewerOpen = true;
  }

  async resetServiceWorker() {
    const account = this.authService.currentAccount;
    const serviceWorkerResetInformation = {
      userId: account?.homeAccountId || account?.username || account?.localAccountId || "unknown",
      timestamp: new Date().toString(),
      platform: navigator.userAgent,
      appVersion: environment.version,
      result: ""
    };
    this.isResettingConnection = true;
    this.logger.info("Unregistering service worker...");

    try {

      const cacheNames = (await caches?.keys()) || [];
      await Promise.all(cacheNames.map(key => caches.delete(key)));

      const serviceWorkerRegistrations = await navigator.serviceWorker.getRegistrations();
      serviceWorkerRegistrations.forEach(registration => registration.unregister());

      this.logger.info("Service worker(s) cleared and unregistered successfully.");
      this.toast.success("Connection has been reset successfully.");
      serviceWorkerResetInformation.result = "Success";
      this.logger.event("Support:ResetServiceWorker", serviceWorkerResetInformation);
      setTimeout(() => {
        this.reloadApp();
      }, 3000);
    } catch (error) {
      this.logger.error("Failed to reset connection: ", error);
      this.toast.error(
        "Failed to reset connection,",
        "Please try again or contact support if the problem persists."
      );
      serviceWorkerResetInformation.result = "Failure";
      this.logger.event("Support:ResetServiceWorker", serviceWorkerResetInformation);
    } finally {
      this.isResettingConnection = false;
    }
  }
  async onResetConnectionClick(event: Event) {
    if (this.isResettingConnection || this.isCollectingDiagnostics || !this.isOnline) {
      return;
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: "You are about to reset the local service worker. This may resolve data sync or update issues. Continue?",
      header: "Delete",
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: false,
        text: true,
      },
      acceptButtonProps: {
        label: "Confirm",
        severity: "primary",
        text: true
      },
      accept: async () => {
        this.resetServiceWorker();
      },
      acceptButtonStyleClass: "btn-confirm-cancel",
      rejectButtonStyleClass: "btn-reject-cancel"
    });
  }

  onClearDeviceDataClick(event: Event): void {
    if (this.isClearingDeviceData || this.isResettingConnection || this.isCollectingDiagnostics || !this.isOnline) {
      return;
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: "This will clear this app's data from your device. <strong>Any unsynced inspections will be permanently lost.</strong>",
      header: "Clear Device Data?",
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: false,
        text: true,
      },
      acceptButtonProps: {
        label: "Confirm",
        severity: "primary",
        text: true
      },
      accept: async () => {
        await this.clearDeviceData();
      },
      acceptButtonStyleClass: "btn-confirm-cancel",
      rejectButtonStyleClass: "btn-reject-cancel"
    });
  }

  async clearDeviceData(): Promise<void> {
    this.isClearingDeviceData = true;

    try {
      await this.databaseService.clearAllData();
      this.toast.success("Device data cleared.", "Reloading the app...");
      setTimeout(() => {
        this.reloadApp();
      }, 1500);
    } catch (error) {
      this.logger.error("Failed to clear device data:", error);
      this.toast.error(
        "Failed to clear device data",
        "Please try again or contact support if the problem persists."
      );
      this.isClearingDeviceData = false;
    }
  }

  public reloadApp() { // abstracting this into a method so we can spy on it in tests
    this.window.location.reload();
  }

  copySupportDataToClipboard(): void {
    const data = this.userId;
    navigator.clipboard.writeText(data)
      .then(() => {
        this.toast.success(
          "User ID copied to clipboard",
          "Please include this in your communication with support to help us assist you better."
        );
      })
      .catch(() => {
        this.toast.error(
          "Failed to copy user id to clipboard",
        );
      });
  }
}

